import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { CreditCard, Loader2, Lock, Store, Truck, Wallet } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import ArtworkPicker, { type Artwork } from '@/components/order/ArtworkPicker'
import PickupDetails from '@/components/order/PickupDetails'
import Cta from '@/components/storefront/Cta'
import OptionCard from '@/components/storefront/OptionCard'
import AddressBook from '@/components/storefront/checkout/AddressBook'
import CheckoutSteps from '@/components/storefront/checkout/CheckoutSteps'
import MobileCheckoutBar from '@/components/storefront/checkout/MobileCheckoutBar'
import { money, priceRowLabel } from '@/lib/money'
import type { OptionSets, ShippingQuote, WalletInfo } from '@/types'
import { useAuth } from '@/context/AuthContext'
import { useCart, pieces, linePriceRows, lineSubtotal, cartArtworks, type CartItem } from '@/context/CartContext'
import { artworkPayload, fontPayload } from '@/lib/artwork'
import { incompleteRows } from '@/lib/personalization'
import { missingRequired, useOptionGroups } from '@/lib/options'
import DeliveryDatePicker from '@/components/order/DeliveryDatePicker'
import { classifyDelivery, urgentRateOn, useDeliveryQuote } from '@/lib/delivery'
import { Field, Input } from '@/components/ui/Field'
import { Select } from '@/components/ui/Select'
import { useToast } from '@/components/ui/toast'
import { useAddresses, useShippingInfo } from '@/lib/addresses'
import { formatDate } from '@/lib/date'
import { SELF_PICKUP } from '@/lib/pickupHours'
import { cn } from '@/lib/cn'

/** One screen per decision: date, then delivery, then payment. */
type Phase = 'date' | 'delivery' | 'payment'

const HEADER_STEP: Record<Phase, 2 | 3 | 4> = { date: 2, delivery: 3, payment: 4 }

export default function Checkout() {
  const { user } = useAuth()
  const { items, selectedItems, removeMany, selectedPieces } = useCart()
  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()
  const optionGroups = useOptionGroups()

  // The customer picks when they need it (client request 2026-10-06); the
  // server starts production on the first day with room and decides urgency.
  const [deliveryDate, setDeliveryDate] = useState<string | null>(null)
  const [deliveryMode, setDeliveryMode] = useState<'pickup' | 'courier' | null>(null)
  const [courier, setCourier] = useState('')
  const [addressId, setAddressId] = useState<number | null>(null)
  const [jobName, setJobName] = useState('')
  const [notes, setNotes] = useState('')
  const [payMethod, setPayMethod] = useState<'wallet' | 'direct'>('wallet')
  // A refresh falls back to the date screen - all state is in memory.
  const [phase, setPhase] = useState<Phase>('date')
  // Inline errors only appear after a continue attempt on that screen, so a
  // screen never opens covered in red.
  const [attempted, setAttempted] = useState(false)
  // True from the moment a direct payment is accepted until the browser has
  // left for the gateway - keeps the button spinning and the redirect guard
  // below from bouncing back to /products after the cart clears.
  const [redirecting, setRedirecting] = useState(false)

  const screenRef = useRef<HTMLDivElement>(null)

  const optionSets = useQuery({
    queryKey: ['options'],
    queryFn: async () => (await api.get<{ data: OptionSets }>('/options')).data.data,
  })

  const wallet = useQuery({
    queryKey: ['wallet'],
    queryFn: async () => (await api.get<WalletInfo>('/wallet')).data,
  })

  const addresses = useAddresses()
  const shippingInfo = useShippingInfo()
  const pickup = shippingInfo.data?.pickup
  // Optimistic while shipping info loads; the server refuses a pickup order
  // regardless if the admin has switched it off.
  const pickupOffered = shippingInfo.data ? shippingInfo.data.pickup.enabled : true

  // With pickup off, courier is the only option: pick it, and undo a pickup
  // click that landed before shipping info arrived.
  useEffect(() => {
    if (shippingInfo.data && !shippingInfo.data.pickup.enabled && deliveryMode !== 'courier') {
      setDeliveryMode('courier')
    }
  }, [shippingInfo.data, deliveryMode])

  const selectedAddress = addresses.data?.find((a) => a.id === addressId) ?? null

  // The quote is keyed by courier, destination state and cart contents - each
  // courier has its own rate card. The server recomputes the fee at checkout
  // regardless, so the number itself is display only - but the customer still
  // consents to the total it feeds, so screenError blocks progression until
  // the quote is in hand.
  const itemsSignature = selectedItems.map((item) => `${item.productId}:${pieces(item)}`).join(',')
  const quote = useQuery({
    queryKey: ['shipping-quote', courier, selectedAddress?.state, itemsSignature],
    enabled: deliveryMode === 'courier' && Boolean(courier) && Boolean(selectedAddress),
    queryFn: async () =>
      (
        await api.post<ShippingQuote>('/shipping/quote', {
          courier,
          state: selectedAddress!.state,
          items: selectedItems.map((item) => ({ product_id: item.productId, qty: pieces(item) })),
        })
      ).data,
  })

  const tierName = user?.tier?.name ?? wallet.data?.tier?.name ?? selectedItems[0]?.tier_prices[0]?.tier ?? 'member'
  // Urgency and its price follow from the delivery date, by the server's own
  // rule: classifyDelivery mirrors DeliveryQuoteService::classify, and each
  // date carries its own rate per product ("makin cepat makin mahal"). The
  // server re-prices at placement.
  const deliveryQuote = useDeliveryQuote(selectedItems.map((item) => ({ productId: item.productId, pieces: pieces(item) })))
  const urgent = Boolean(deliveryDate && deliveryQuote.data && classifyDelivery(deliveryDate, deliveryQuote.data).urgent)
  const lineUrgentRate = (item: (typeof selectedItems)[number]) => urgentRateOn(deliveryQuote.data, deliveryDate, item.productId)
  const rushDelta = selectedItems.reduce((sum, item) => sum + pieces(item) * lineUrgentRate(item), 0)
  // Each date's own rush for the calendar's note: what picking it adds.
  const rushOn = (date: string) =>
    selectedItems.reduce((sum, item) => sum + pieces(item) * urgentRateOn(deliveryQuote.data, date, item.productId), 0)

  const subtotal = selectedItems.reduce(
    (sum, item) => sum + lineSubtotal(item, tierName, optionGroups.data, lineUrgentRate(item)),
    0,
  )
  const deliveryFee = deliveryMode === 'courier' ? (quote.data?.delivery_fee ?? null) : 0
  const grandTotal = subtotal + (deliveryFee ?? 0)
  const walletShort = (wallet.data?.balance ?? 0) < grandTotal

  // Couriers come from the admin's rate cards; the option set only fills in
  // while shipping info is still loading.
  const courierOptions =
    shippingInfo.data?.couriers ??
    (optionSets.data?.delivery_methods ?? []).filter((method) => method !== SELF_PICKUP)

  // Keyed by cart line, seeded from files already uploaded on the product
  // page (signed-in customers upload there; the cart carries the reference).
  // Anything attached or replaced here stays local to this checkout.
  const [artwork, setArtwork] = useState<Record<string, Artwork[]>>(() =>
    Object.fromEntries(items.map((item) => [item.uid, cartArtworks(item)])),
  )
  // The font behind each line's names, seeded and kept the same way. Only a
  // line with names asks for one (client request 2026-10-14).
  const [fonts, setFonts] = useState<Record<string, Artwork[]>>(() =>
    Object.fromEntries(items.map((item) => [item.uid, item.fonts ?? []])),
  )
  const lineFonts = (item: CartItem) => fonts[item.uid] ?? item.fonts ?? []
  const missingFont = selectedItems.find((item) => item.personalizations.length > 0 && lineFonts(item).length === 0)

  const screenError = (which: Phase): string | null => {
    // Personalized items must have every row filled - the server rejects
    // partial coverage, and the rows are only editable back in the cart.
    if (selectedItems.some((item) => incompleteRows(item.personalizations).length > 0)) {
      return 'Some name & number rows are incomplete - finish them in your cart first'
    }
    // Same for a compulsory option left empty: the server refuses the item,
    // and options can only be chosen again from the product page.
    if (selectedItems.some((item) => missingRequired(item.spec, item.required_options, optionGroups.data).length > 0)) {
      return 'An item is missing a compulsory option - fix it in your cart first'
    }
    if (which === 'date') {
      return deliveryDate ? null : 'Pick a delivery date to continue'
    }
    if (which === 'delivery') {
      if (!deliveryMode) return pickupOffered ? 'Choose self pickup or courier delivery' : 'Choose a courier'
      if (deliveryMode === 'courier' && !courier) return 'Choose a courier'
      if (deliveryMode === 'courier' && addressId == null)
        return 'Add a delivery address so we can quote your delivery fee'
      // The fee is part of the total the customer agrees to, and the server
      // charges it whether or not this screen managed to show it. Without
      // this, a pending or failed quote collapses to a fee of zero, the
      // wallet-sufficiency check passes against the wrong total, and the
      // server rejects the order with a generic failure.
      if (deliveryMode === 'courier' && quote.isPending)
        return 'Getting your delivery rate - one moment'
      if (deliveryMode === 'courier' && quote.isError)
        return pickupOffered
          ? 'We could not get a delivery rate - try again or choose self pickup'
          : 'We could not get a delivery rate - please try again'
      return null
    }
    if (deliveryMode === 'courier' && (quote.isPending || quote.isError)) {
      return 'Your delivery rate is missing - go back to the delivery step and try again'
    }
    // Checked at payment, where the order summary (and its font picker) is
    // on screen on phones too. The server refuses the same.
    if (missingFont) {
      return `Attach the font file for the names on ${missingFont.name} - in the order summary`
    }
    return payMethod === 'wallet' && walletShort
      ? 'Wallet balance is not enough - top up or choose pay directly'
      : null
  }

  const currentError = attempted ? screenError(phase) : null

  const goTo = (next: Phase) => {
    setAttempted(false)
    setPhase(next)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const placeOrder = useMutation({
    mutationFn: async () =>
      (
        await api.post('/orders', {
          payment_method: payMethod,
          delivery_date: deliveryDate,
          job_name: jobName.trim() || null,
          notes: notes || null,
          delivery_method: deliveryMode === 'pickup' ? SELF_PICKUP : courier,
          address_id: deliveryMode === 'courier' ? addressId : null,
          items: selectedItems.map((item) => ({
            product_id: item.productId,
            size_matrix: Object.fromEntries(Object.entries(item.sizeMatrix).filter(([, v]) => v > 0)),
            // Falls back to the line's own reference, so a Buy again line whose
            // file came off the old order survives even if this screen's
            // artwork map was seeded without it.
            ...artworkPayload(artwork[item.uid] ?? cartArtworks(item), item.artworkName),
            ...(item.personalizations.length > 0 ? fontPayload(lineFonts(item)) : {}),
            spec: item.spec,
            personalizations: item.personalizations,
          })),
        })
      ).data,
    onSuccess: (data) => {
      // Only the lines that were actually ordered. Anything the customer left
      // unticked is theirs to keep working on.
      removeMany(selectedItems.map((item) => item.uid))
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['wallet'] })

      if (data.payment?.checkout_url) {
        // Direct payment: the order exists but is not confirmed until the
        // gateway's webhook says so. The return URL cannot carry our id, so
        // stash it for the return page to poll - same dance as wallet top-ups.
        setRedirecting(true)
        sessionStorage.setItem('mpg_pending_order_payment', String(data.payment.payment_id))
        window.location.href = data.payment.checkout_url
        return
      }

      toast.success('Order placed', 'Paid from wallet. Confirmation email sent.')
      navigate(`/account/orders/${data.data.id}`)
    },
    onError: (err) => toast.error('Order failed', errorMessage(err)),
  })

  // The primary button never sits dead behind a disabled state: an incomplete
  // screen shows its inline error and scrolls it into view instead.
  const primaryAction = () => {
    if (placeOrder.isPending || redirecting) return
    setAttempted(true)
    if (screenError(phase)) {
      screenRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }
    if (phase === 'date') goTo('delivery')
    else if (phase === 'delivery') goTo('payment')
    else placeOrder.mutate()
  }

  // Guard isPending/isSuccess/redirecting: the cart is cleared right after
  // payment, and this redirect must not race the navigation to the freshly
  // placed order or the hand-off to the gateway.
  if (selectedItems.length === 0 && !placeOrder.isPending && !placeOrder.isSuccess && !redirecting) {
    // Back to the cart rather than the catalogue when there are lines to tick:
    // the customer unticked everything and needs to see that, not a product
    // grid that looks like their cart was thrown away.
    return <Navigate to={items.length > 0 ? '/cart' : '/products'} replace />
  }

  const onPayment = phase === 'payment'
  const ctaLabel =
    phase === 'date' ? 'Continue' : phase === 'delivery' ? 'Continue to payment' : payMethod === 'wallet' ? 'Place order' : 'Pay now'

  const heading =
    phase === 'date' ? 'Delivery date' : phase === 'delivery' ? 'Delivery' : 'Payment'
  const subheading =
    phase === 'date'
      ? 'When do you need your order?'
      : phase === 'delivery'
        ? 'How should we get your order to you?'
        : 'Almost done - choose how to pay and place your order'

  return (
    <div className="mx-auto w-full max-w-6xl px-5 pb-28 pt-8 sm:pt-10 lg:pb-10">
      <CheckoutSteps
        current={HEADER_STEP[phase]}
        onStepClick={(step) => goTo(step === 2 ? 'date' : 'delivery')}
      />

      <h1 className="mt-8 text-3xl font-semibold">{heading}</h1>
      <p className="mt-1 text-sm text-foreground/60">{subheading}</p>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3" ref={screenRef}>
        {/* Screens stay mounted and toggle with `hidden`, so the calendar
            month, address selection and queries survive back-and-forward. */}
        <div className={cn('lg:col-span-2', phase !== 'date' && 'hidden')}>
          <div className={cn('box p-5', currentError && phase === 'date' && 'border-danger/40')}>
            <p className="font-bold">Choose your delivery date</p>
            <p className="mt-0.5 text-xs text-foreground/60">
              We start printing your {selectedPieces} pcs on the first available day. Pick the day you need it - a date
              shorter than our normal production time is done as urgent, at the urgent rate.
            </p>
            {phase === 'date' && currentError && (
              <p className="mt-1 text-xs font-semibold text-danger">{currentError}</p>
            )}
            <div className="mt-4">
              <DeliveryDatePicker
                quote={deliveryQuote.data}
                loading={deliveryQuote.isLoading}
                value={deliveryDate}
                onChange={setDeliveryDate}
                pieces={selectedPieces}
                countsQuota
                variant="storefront"
                urgentNote={rushDelta > 0 ? `+${money(rushDelta)}` : null}
                rushFor={(date) => {
                  const total = rushOn(date)
                  return total > 0 ? `+${money(total)}` : null
                }}
              />
            </div>

            <Link to="/cart" className="mt-4 inline-block text-xs font-semibold text-foreground/60 hover:text-foreground">
              &lt; Back to cart
            </Link>
          </div>
        </div>

        <div className={cn('lg:col-span-2', phase !== 'delivery' && 'hidden')}>
          <div className={cn('box p-5', currentError && phase === 'delivery' && 'border-danger/40')}>
            <p className="font-bold">How should we deliver your order?</p>
            {phase === 'delivery' && currentError && (
              <p className="mt-1 text-xs font-semibold text-danger">{currentError}</p>
            )}
            <div className={cn('mt-4 grid gap-3', pickupOffered && 'sm:grid-cols-2')}>
              {pickupOffered && (
                <OptionCard
                  selected={deliveryMode === 'pickup'}
                  onSelect={() => setDeliveryMode('pickup')}
                  icon={<Store className="size-4 text-primary" />}
                  title="Self pickup"
                  badge="Free"
                  description="Collect from our workshop when your order is done"
                />
              )}
              <OptionCard
                selected={deliveryMode === 'courier'}
                onSelect={() => setDeliveryMode('courier')}
                icon={<Truck className="size-4 text-primary" />}
                title="Courier delivery"
                description="Delivered to your address - fee quoted by weight and area"
              />
            </div>

            {deliveryMode === 'pickup' && pickup && <PickupDetails pickup={pickup} className="mt-3" />}

            {deliveryMode === 'courier' && (
              <div className="mt-3 space-y-4">
                <Field label="Courier">
                  <Select
                    value={courier}
                    onChange={setCourier}
                    placeholder="Choose a courier..."
                    options={courierOptions.map((method) => ({ value: method, label: method }))}
                  />
                </Field>

                <div>
                  <p className="mb-2 text-xs font-bold uppercase tracking-wide text-foreground/50">Deliver to</p>
                  <AddressBook value={addressId} onChange={setAddressId} />
                </div>

                {!courier ? (
                  <p className="text-xs text-foreground/60">Choose a courier to see your delivery fee.</p>
                ) : selectedAddress == null ? (
                  <p className="text-xs text-foreground/60">Add an address to see your delivery fee.</p>
                ) : quote.isPending ? (
                  <p className="flex items-center gap-2 text-xs text-foreground/60">
                    <Loader2 className="size-3 animate-spin" /> Getting your delivery rate...
                  </p>
                ) : quote.isError ? (
                  <p className="text-xs font-semibold text-danger">
                    {errorMessage(quote.error)}{' '}
                    <button type="button" className="underline" onClick={() => quote.refetch()}>
                      Try again
                    </button>
                  </p>
                ) : quote.data ? (
                  <p className="text-xs text-foreground/70">
                    Delivery fee ({(quote.data.weight_grams / 1000).toFixed(2)} kg):{' '}
                    <span className="font-bold text-primary">{money(quote.data.delivery_fee)}</span>
                  </p>
                ) : null}
              </div>
            )}

            {/* Prints as the job sheet's title; blank keeps the sheet's own title. */}
            <div className="mt-4">
              <Field label="Job name (optional)">
                <Input value={jobName} maxLength={150} onChange={(e) => setJobName(e.target.value)} placeholder="e.g. Jersi Kelab Bola Kerian 2026" />
              </Field>
            </div>

            <div className="mt-4">
              <Field label="Notes for production (optional)">
                <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. nama & nombor di belakang jersi" />
              </Field>
            </div>

            <button
              type="button"
              onClick={() => goTo('date')}
              className="mt-4 text-xs font-semibold text-foreground/60 hover:text-foreground"
            >
              &lt; Back to delivery date
            </button>
          </div>
        </div>

        <div className={cn('lg:col-span-2', !onPayment && 'hidden')}>
          <div className={cn('box p-5', currentError && onPayment && 'border-danger/40')}>
            <p className="font-bold">How would you like to pay?</p>
            <p className="mt-0.5 text-xs text-foreground/60">Pay with your wallet balance or through the payment gateway</p>
            {onPayment && currentError && <p className="mt-1 text-xs font-semibold text-danger">{currentError}</p>}
            <div className="mt-4 space-y-2">
              <OptionCard
                selected={payMethod === 'wallet'}
                onSelect={() => setPayMethod('wallet')}
                icon={<Wallet className="size-4 text-primary" />}
                title="Pay from wallet"
              >
                <span className="mt-2 flex justify-between text-xs">
                  <span className="text-foreground/60">Balance</span>
                  <span className="font-semibold">{money(wallet.data?.balance)}</span>
                </span>
                <span className="mt-1 flex justify-between text-xs">
                  <span className="text-foreground/60">After payment</span>
                  <span className={cn('font-semibold', walletShort && 'text-danger')}>
                    {money((wallet.data?.balance ?? 0) - grandTotal)}
                  </span>
                </span>
              </OptionCard>
              <OptionCard
                selected={payMethod === 'direct'}
                onSelect={() => setPayMethod('direct')}
                icon={<CreditCard className="size-4 text-primary" />}
                title="Pay directly"
                description="FPX online banking, cards and e-wallets via the payment gateway."
              />
            </div>
            {payMethod === 'wallet' && walletShort && (
              <p className="mt-2 text-xs font-semibold text-danger">
                Insufficient balance -{' '}
                <Link to="/account/wallet/topup" className="underline">
                  top up your wallet
                </Link>{' '}
                or pay directly instead.
              </p>
            )}
            <button
              type="button"
              onClick={() => goTo('delivery')}
              className="mt-4 text-xs font-semibold text-foreground/60 hover:text-foreground"
            >
              &lt; Back to delivery
            </button>
          </div>
        </div>

        {/* On phones the summary only appears at payment - earlier screens
            keep a single action: the bottom bar. Desktop always shows it. */}
        <div className={cn(!onPayment && 'hidden lg:block')}>
          <div className="sticky top-[72px] sm:top-[86px]">
            <div className="box p-5">
              <p className="text-xs font-bold uppercase tracking-wide text-foreground/50">Order summary</p>
              <div className="mt-3 divide-y divide-foreground/5 text-sm">
                {selectedItems.map((item) => {
                  const qty = pieces(item)
                  const rows = linePriceRows(item, tierName, optionGroups.data, lineUrgentRate(item))
                  return (
                    <div key={item.uid} className="flex justify-between gap-3 py-2">
                      <span className="text-foreground/70">
                        {item.name}
                        <span className="block text-xs text-foreground/50">
                          {rows.length > 1
                            ? rows.map((row) => (
                                <span key={row.group ?? ''} className="block">
                                  {priceRowLabel({ group: row.group, qty: row.qty, price: row.unit })}
                                </span>
                              ))
                            : `${qty} pcs x ${money(rows[0]?.unit ?? 0)}`}{' '}
                          ({tierName})
                        </span>
                        <ArtworkPicker
                          value={artwork[item.uid] ?? []}
                          fallbackName={item.artworkName}
                          onChange={(next) => setArtwork((prev) => ({ ...prev, [item.uid]: next }))}
                        />
                        {item.personalizations.length > 0 && (
                          <span className="mt-1.5 block">
                            <span className="block text-2xs font-semibold text-foreground/60">
                              Font for the names <span className="text-danger">*</span>
                            </span>
                            <ArtworkPicker
                              kind="font"
                              value={lineFonts(item)}
                              onChange={(next) => setFonts((prev) => ({ ...prev, [item.uid]: next }))}
                            />
                          </span>
                        )}
                      </span>
                      <span className="font-semibold">{money(lineSubtotal(item, tierName, optionGroups.data, lineUrgentRate(item)))}</span>
                    </div>
                  )
                })}
              </div>
              <div className="space-y-1.5 border-t border-foreground/10 pt-3 text-sm">
                <p className="flex justify-between text-xs text-foreground/60">
                  <span>Subtotal ({selectedPieces} pcs)</span>
                  <span className="font-semibold text-foreground">{money(subtotal)}</span>
                </p>
                {/* The subtotal already contains this; the line only explains it. */}
                {urgent && rushDelta > 0 && (
                  <p className="flex justify-between text-xs font-semibold text-danger">
                    <span>Urgent service</span>
                    <span>included above (+{money(rushDelta)})</span>
                  </p>
                )}
                <p className="flex justify-between text-xs text-foreground/60">
                  <span>Delivery</span>
                  <span className={cn('font-semibold', deliveryMode === 'pickup' ? 'text-success' : 'text-foreground')}>
                    {deliveryMode === 'pickup'
                      ? 'Free'
                      : deliveryMode === 'courier'
                        ? selectedAddress == null
                          ? 'Add address'
                          : deliveryFee == null
                            ? '...'
                            : money(deliveryFee)
                        : '-'}
                  </span>
                </p>
                {deliveryDate && (
                  <p className="flex justify-between text-xs text-foreground/60">
                    <span>Delivery date</span>
                    <span className="flex items-center gap-1.5 font-semibold text-foreground">
                      {formatDate(deliveryDate)}
                      {urgent && (
                        <span className="rounded-full bg-danger px-1.5 py-px text-[10px] font-bold uppercase leading-tight text-white">Urgent</span>
                      )}
                    </span>
                  </p>
                )}
                <p className="flex justify-between pt-1 text-base">
                  <span className="font-bold">Total</span>
                  <span className="font-extrabold text-primary">{money(grandTotal)}</span>
                </p>
              </div>

              {/* Below lg the fixed bottom bar is the single action, so the
                  in-card button would be a duplicate right above it. */}
              <div className="hidden lg:block">
                <Cta
                  onClick={primaryAction}
                  disabled={selectedPieces === 0}
                  loading={placeOrder.isPending || redirecting}
                  fullWidth
                  arrow
                  className="mt-4"
                >
                  {ctaLabel} {onPayment && money(grandTotal)}
                </Cta>
              </div>
              {onPayment && (
                <p className="mt-2 flex items-center justify-center gap-1 text-center text-2xs text-foreground/50">
                  <Lock className="size-3" /> Secure payment - SSL encrypted
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <MobileCheckoutBar
        total={grandTotal}
        label={ctaLabel}
        loading={placeOrder.isPending || redirecting}
        onSubmit={primaryAction}
      />

      {redirecting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="rounded-lg border border-foreground/10 bg-background px-8 py-6 text-center shadow-xl">
            <Loader2 className="mx-auto size-8 animate-spin text-primary" />
            <p className="mt-3 text-sm font-semibold">Taking you to the payment page...</p>
            <p className="mt-1 text-xs text-foreground/60">Do not close this tab.</p>
          </div>
        </div>
      )}
    </div>
  )
}
